"use client";
import { useCallback, useEffect, useState } from 'react';
import { LocationStatus } from '@/types/nearbyMap';
import { toast } from 'react-toastify';
import { useSearchParams } from 'next/navigation';

// Улаанбаатарын default байршил
const DEFAULT_POSITION = { lat: 47.9186, lng: 106.9177 };

const extractPositionFromURL = (
  searchParams: URLSearchParams
): { lat: number; lng: number } | null => {
  const latParam = searchParams.get('lat');
  const lngParam = searchParams.get('lng');

  if (latParam && lngParam) {
    const lat = parseFloat(latParam);
    const lng = parseFloat(lngParam);

    if (!isNaN(lat) && !isNaN(lng)) {
      return { lat, lng };
    }
  }

  return null;
};

const getInitialPosition = (
  searchParams: URLSearchParams
): { lat: number; lng: number } => {
  const urlPosition = extractPositionFromURL(searchParams);
  return urlPosition || DEFAULT_POSITION;
};

let hasShownPermissionToast = false;

export const useGeolocation = () => {
  const searchParams = useSearchParams();
  const [currentPosition, setCurrentPosition] = useState<{
    lat: number;
    lng: number;
  } | null>(() => getInitialPosition(searchParams));
  const [locationStatus, setLocationStatus] = useState<LocationStatus>(() => {
    // URL дээр coordinates байвал granted status өгөх, үгүй бол loading
    const urlPosition = extractPositionFromURL(searchParams);
    return urlPosition ? 'granted' : 'loading';
  });
  const [isOpen, setIsOpen] = useState(false);

  // Modal-г зөвхөн хаах - дахин нээхгүй (хэрэглэгч Татгалзах дарсан үед loop үүсгэхгүй)
  useEffect(() => {
    if (locationStatus !== 'prompt') {
      setIsOpen(false);
    }
  }, [locationStatus]);

  const getUserLocation = useCallback(() => {
    setLocationStatus('loading');
    if (!navigator.geolocation) {
      setLocationStatus('unavailable');
      setCurrentPosition(DEFAULT_POSITION);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const pos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setCurrentPosition(pos);
        setLocationStatus('granted');
        setIsOpen(false);
        hasShownPermissionToast = false;
      },
      (error) => {
        console.error('Geolocation error:', error);
        setCurrentPosition(DEFAULT_POSITION);

        if (error.code === 1) {
          setLocationStatus('denied');
          toast.error(
            'Байршлын зөвшөөрөл хаалттай байна. Хөтөч дээрээ байршлын зөвшөөрлийг нээнэ үү.'
          );
        } else {
          setLocationStatus('unavailable');
          toast.error(
            'Байршил авах боломжгүй байна. GPS идэвхтэй эсэхийг шалгана уу.'
          );
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  }, []);

  useEffect(() => {
    // URL дээр coordinates байгаа бол geolocation permission шалгахгүй
    const urlPosition = extractPositionFromURL(searchParams);
    if (urlPosition) {
      setCurrentPosition(urlPosition);
      return;
    }

    if ('permissions' in navigator) {
      navigator.permissions
        .query({ name: 'geolocation' as PermissionName })
        .then((permissionStatus) => {
          if (permissionStatus.state === 'granted') {
            setLocationStatus('granted');
            getUserLocation();
          } else if (permissionStatus.state === 'prompt') {
            setLocationStatus('prompt');
            setIsOpen(true); // permission status мэдэгдсэний дараа л modal нээх
          } else {
            setLocationStatus('denied');
            setCurrentPosition(DEFAULT_POSITION);
            if (!hasShownPermissionToast) {
              hasShownPermissionToast = true;
              toast.error(
                'Байршлын зөвшөөрөл хаалттай байна. Хөтөч дээрээ байршлын зөвшөөрлийг нээнэ үү.'
              );
            }
          }

          permissionStatus.onchange = () => {
            if (permissionStatus.state === 'granted') {
              setIsOpen(false);
              hasShownPermissionToast = false;
              getUserLocation();
            } else {
              const newStatus = permissionStatus.state === 'prompt' ? 'prompt' : 'denied';
              setLocationStatus(newStatus);
              if (newStatus === 'prompt') setIsOpen(true);
              setCurrentPosition(DEFAULT_POSITION);
            }
          };
        })
        .catch(() => {
          setCurrentPosition(DEFAULT_POSITION);
        });
    } else {
      // Permissions API байхгүй бол modal нээж асуух
      setLocationStatus('prompt');
      setIsOpen(true);
    }
  }, [getUserLocation, searchParams]);

  return {
    currentPosition,
    setCurrentPosition,
    locationStatus,
    isOpen,
    setIsOpen,
    getUserLocation,
  };
};
